import React, {FC, memo, useState} from "react";
import {Button, Checkbox, Space, Tooltip} from "antd";
import {FormikProps} from "formik";
import {EditOutlined, EyeOutlined, SaveOutlined, SendOutlined, FilePdfOutlined} from "@ant-design/icons";
import {DbTemplate} from "./Email";
import {
    Booking,
    EditTemplateInput,
    MailDataType,
    RenderBatchLetterPdfForBookingsInput,
    RenderBatchLetterPdfForCourseInput,
    RenderTemplateForLetterPdfInput,
    SendMailToSelectionOfCourseInput,
    TemplateType,
    useGetCompanySettingsQuery,
    useRenderBatchTemplateForBookingsLetterPdfLazyQuery,
    useRenderBatchTemplateForCourseLetterPdfLazyQuery,
    useRenderTemplateForLetterPdfLazyQuery,
} from "../../../generated/graphql";
import {SelectionType} from "./EmailTemplatePicker";
import {SendInvoiceToBookings, SendMailToSelectionOfStaff, SendToAttendees, SendToBookers, SendToBooking, SendToCourse, SendToScheduledPayments} from "./SendMailHandlers/SendMailToSelectionHandlers";
import {NotifySelection} from "./SendMailHandlers/CourseBookingTypeSelector";
import LetterPdfPreviewModal from "./letterPdf/LetterPdfPreviewModal";
import LetterPdfBatchPreviewModal from "./letterPdf/LetterPdfBatchPreviewModal";
import {LetterPdfData} from "./letterPdf/generateLetterPdf";
import {mapRenderedToLetterPdfData} from "./letterPdf/letterPdfDataMapper";

/** Vergleichbare Textblöcke: `null` und `undefined` bedeuten beide "leer". */
type TemplateTextFields = Pick<EditTemplateInput, "subject" | "headerText" | "footerText" | "signature">;

const normalizeTemplateFields = (source: TemplateTextFields | undefined): TemplateTextFields => ({
    subject: source?.subject ?? "",
    headerText: source?.headerText ?? "",
    footerText: source?.footerText ?? "",
    signature: source?.signature ?? "",
});

type AttachmentStatus = {
    enabled: boolean;
    reason: string;
};

type EmailFooterProps = {
    dbTemplate: DbTemplate;
    editMode: boolean;
    setEditMode: (editMode: boolean) => void;
    formikProps?: FormikProps<EditTemplateInput & {id: string}>;
    /** Ungespeicherter Entwurf aus dem Parent — einzige Quelle fuer Vorschau und Versand (#820). */
    draft?: EditTemplateInput;
    selection: Array<SelectionType>;
    mailDataType: MailDataType;
    closeAll?: () => void;
    attachments?: SendMailToSelectionOfCourseInput["attachments"];
    disabledSendButton?: boolean;
    notifySelection?: Array<NotifySelection>;
    setSelectedTemplateUndefined?: () => void;
    showInvoiceAttachments?: boolean;
    invoiceAttachmentStatus?: AttachmentStatus;
    reversalInvoiceAttachmentStatus?: AttachmentStatus;
    attachInvoices?: boolean;
    setAttachInvoices?: (value: boolean) => void;
    attachReversalInvoices?: boolean;
    setAttachReversalInvoices?: (value: boolean) => void;
};

const EmailFooter: FC<EmailFooterProps> = ({
    dbTemplate,
    editMode,
    setEditMode,
    formikProps,
    draft,
    selection,
    mailDataType,
    closeAll,
    attachments,
    disabledSendButton,
    notifySelection,
    setSelectedTemplateUndefined,
    showInvoiceAttachments,
    invoiceAttachmentStatus,
    reversalInvoiceAttachmentStatus,
    attachInvoices,
    setAttachInvoices,
    attachReversalInvoices,
    setAttachReversalInvoices,
}) => {
    const [letterPdfModalVisible, setLetterPdfModalVisible] = useState(false);
    const [letterData, setLetterData] = useState<LetterPdfData | null>(null);
    const [batchLetterPdfModalVisible, setBatchLetterPdfModalVisible] = useState(false);
    const [batchLetterDataList, setBatchLetterDataList] = useState<LetterPdfData[]>([]);

    // #514: ownerCountry als Länder-Fallback für das Steuer-ID-Label im
    // Brief-Footer — gleiche (taxId, Land)-Ableitung wie das Rechnungs-PDF.
    // Der Brief-Render-Payload transportiert das Land nicht; die ohnehin
    // gecachte Company-Settings-Query liefert es clientseitig.
    const {data: companySettingsData} = useGetCompanySettingsQuery();
    const senderCountry = companySettingsData?.companySettings?.ownerCountry ?? undefined;
    const withSenderCountry = (mapped: LetterPdfData): LetterPdfData => ({...mapped, senderCountry});

    const [renderTemplateForLetterPdf, {loading: renderLetterPdfLoading}] = useRenderTemplateForLetterPdfLazyQuery({
        onCompleted: (data) => {
            if (data?.renderTemplateForLetterPdf) {
                setLetterData(withSenderCountry(mapRenderedToLetterPdfData(data.renderTemplateForLetterPdf)));
                setLetterPdfModalVisible(true);
            }
        },
        fetchPolicy: "network-only",
    });

    const [renderBatchTemplateForCourseLetterPdf, {loading: renderBatchLetterPdfLoading}] = useRenderBatchTemplateForCourseLetterPdfLazyQuery({
        onCompleted: (data) => {
            if (data?.renderBatchTemplateForCourseLetterPdf?.items) {
                const mappedItems = data.renderBatchTemplateForCourseLetterPdf.items.map((item) => withSenderCountry(mapRenderedToLetterPdfData(item)));
                setBatchLetterDataList(mappedItems);
                setBatchLetterPdfModalVisible(true);
            }
        },
        fetchPolicy: "network-only",
    });

    const [renderBatchTemplateForBookingsLetterPdf, {loading: renderBatchBookingsLetterPdfLoading}] = useRenderBatchTemplateForBookingsLetterPdfLazyQuery({
        onCompleted: (data) => {
            if (data?.renderBatchTemplateForBookingsLetterPdf?.items) {
                const mappedItems = data.renderBatchTemplateForBookingsLetterPdf.items.map((item) => withSenderCountry(mapRenderedToLetterPdfData(item)));
                setBatchLetterDataList(mappedItems);
                setBatchLetterPdfModalVisible(true);
            }
        },
        fetchPolicy: "network-only",
    });

    const handleSave = () => {
        if (formikProps) {
            formikProps.submitForm();
        }
    };

    const isNoTemplate = dbTemplate.data?.template.templateType === TemplateType.NoTemplate;

    // Ungespeicherter Entwurf aus dem Editor. Er bleibt beim Zurückschalten auf die Vorschau
    // erhalten und wird dort auch angezeigt (#814) — also muss auch das Senden aus der Vorschau
    // ihn mitnehmen, sonst zeigt die Vorschau etwas anderes als die Mail.
    // Quelle ist der Entwurf des Parents, nicht ein Snapshot der (beim Moduswechsel neu
    // gemounteten) Formik-Instanz (#820).
    // Beide Seiten gleich normalisieren: ein gespeichertes null (z. B. footerText) landet als null
    // im Entwurf und darf nicht als Änderung gelten.
    const storedFields = normalizeTemplateFields(dbTemplate.data?.template ?? undefined);
    const draftFields: TemplateTextFields | undefined = draft ? normalizeTemplateFields(draft) : undefined;
    // Deckt sich der Entwurf mit der gespeicherten Vorlage, ist er kein "Senden ohne speichern":
    // dann geht auch kein editedTemplateInput raus (identischer Inhalt, korrektes Button-Label).
    const draftDiffersFromStored = Boolean(
        draftFields &&
            (draftFields.subject !== storedFields.subject ||
                draftFields.headerText !== storedFields.headerText ||
                draftFields.footerText !== storedFields.footerText ||
                draftFields.signature !== storedFields.signature),
    );
    const draftInput: EditTemplateInput | undefined = draftDiffersFromStored ? draftFields : undefined;

    /** MailDataTypes bei denen der "Als Brief"-Button angezeigt werden soll */
    const letterPdfSupportedTypes: MailDataType[] = [MailDataType.Booking, MailDataType.Booker, MailDataType.Attendee, MailDataType.ScheduledPayment, MailDataType.Course];
    const showLetterPdfButton = letterPdfSupportedTypes.indexOf(mailDataType) !== -1;

    /**
     * Laedt die gerenderten Template-Daten fuer die Brief-PDF-Vorschau.
     * Bei Course: Batch-Query fuer alle Buchungen des Kurses.
     * Bei Einzelentitaeten: Einzelne Query mit der Entity-ID.
     */
    const handleLetterPdfClick = () => {
        const templateId = dbTemplate.data?.template.id;
        if (!templateId) return;

        // Bei Course: Batch-Query aufrufen
        if (mailDataType === MailDataType.Course) {
            const batchInput: RenderBatchLetterPdfForCourseInput = {
                templateId,
                courseId: selection[0].id,
            };

            // NotifySelection-Flags setzen (Default: notifyBookings=true wenn keine Auswahl vorhanden)
            if (notifySelection && notifySelection.length > 0) {
                batchInput.notifyBookings = notifySelection.indexOf("notifyBookings" as NotifySelection) !== -1;
                batchInput.notifyWaitlist = notifySelection.indexOf("notifyWaitlist" as NotifySelection) !== -1;
                batchInput.notifyCancelations = notifySelection.indexOf("notifyCancelations" as NotifySelection) !== -1;
            } else {
                batchInput.notifyBookings = true;
            }

            // Wenn das Template bearbeitet wurde, editedTemplateInput mitschicken
            if (draftInput) {
                batchInput.editedTemplateInput = draftInput;
            }

            renderBatchTemplateForCourseLetterPdf({variables: {input: batchInput}});
            return;
        }

        // Bei Booking mit mehreren Empfaengern: Batch-Query aufrufen
        if (mailDataType === MailDataType.Booking && selection.length > 1) {
            const batchInput: RenderBatchLetterPdfForBookingsInput = {
                templateId,
                bookingIds: selection.map((s) => s.id),
            };

            // Wenn das Template bearbeitet wurde, editedTemplateInput mitschicken
            if (draftInput) {
                batchInput.editedTemplateInput = draftInput;
            }

            renderBatchTemplateForBookingsLetterPdf({variables: {input: batchInput}});
            return;
        }

        // Bei Einzelentitaeten: Bestehende Logik
        if (selection.length !== 1) return;

        const input: RenderTemplateForLetterPdfInput = {
            templateId,
        };

        // Entity-ID basierend auf dem mailDataType setzen
        if (mailDataType === MailDataType.Booking) {
            input.bookingId = selection[0].id;
        } else if (mailDataType === MailDataType.Booker) {
            input.bookerId = selection[0].id;
        } else if (mailDataType === MailDataType.Attendee) {
            input.attendeeId = selection[0].id;
        } else if (mailDataType === MailDataType.ScheduledPayment) {
            input.scheduledPaymentId = selection[0].id;
        }

        // Wenn das Template bearbeitet wurde, editedTemplateInput mitschicken
        if (draftInput) {
            input.editedTemplateInput = draftInput;
        }

        renderTemplateForLetterPdf({variables: {input}});
    };

    const SendButton = ({editedTemplateInput}: {editedTemplateInput?: EditTemplateInput}) => {
        switch (mailDataType) {
            case MailDataType.Course:
                return (
                    <SendToCourse
                        // selection={selection as Array<Course>}
                        selection={selection}
                        dbTemplateId={dbTemplate.data?.template.id}
                        editedTemplateInput={editedTemplateInput}
                        closeAll={closeAll}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                        notifySelection={notifySelection}
                        attachInvoices={attachInvoices}
                        attachReversalInvoices={attachReversalInvoices}
                    />
                );
            case MailDataType.Booking:
                return (
                    <SendToBooking
                        selection={selection as Array<Booking>}
                        dbTemplateId={dbTemplate.data?.template.id}
                        editedTemplateInput={editedTemplateInput}
                        closeAll={closeAll}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                        attachInvoices={attachInvoices}
                        attachReversalInvoices={attachReversalInvoices}
                    />
                );
            case MailDataType.Booker:
                return (
                    <SendToBookers
                        selection={selection}
                        dbTemplateId={dbTemplate.data?.template.id}
                        editedTemplateInput={editedTemplateInput}
                        closeAll={closeAll}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                    />
                );
            case MailDataType.Attendee:
                return (
                    <SendToAttendees
                        selection={selection}
                        dbTemplateId={dbTemplate.data?.template.id}
                        editedTemplateInput={editedTemplateInput}
                        closeAll={closeAll}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                    />
                );
            case MailDataType.ScheduledPayment:
                return (
                    <SendToScheduledPayments
                        selection={selection}
                        dbTemplateId={dbTemplate.data?.template.id}
                        editedTemplateInput={editedTemplateInput}
                        closeAll={closeAll}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                        attachInvoices={attachInvoices}
                        attachReversalInvoices={attachReversalInvoices}
                    />
                );
            case MailDataType.Invoice:
                return (
                    <SendInvoiceToBookings
                        selection={selection as Array<Booking>}
                        dbTemplateId={dbTemplate.data?.template.id}
                        editedTemplateInput={editedTemplateInput}
                        closeAll={closeAll}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                    />
                );
            case MailDataType.Staff:
                return (
                    <SendMailToSelectionOfStaff
                        selection={selection}
                        dbTemplateId={dbTemplate.data?.template.id}
                        editedTemplateInput={editedTemplateInput}
                        closeAll={closeAll}
                        attachments={attachments}
                        disabledSendButton={disabledSendButton}
                    />
                );
            default:
                return (
                    <Button icon={<SendOutlined />} type="primary">
                        {"Noch nicht implementiert"}
                    </Button>
                );
        }
    };

    if (isNoTemplate) {
        setEditMode(true);
    }

    const InvoiceCheckboxes = () => {
        if (!showInvoiceAttachments) {
            return null;
        }

        // Priorität: Wenn Storno-Rechnungen verfügbar sind, zeige diese, sonst normale Rechnungen
        const showReversalInvoice = reversalInvoiceAttachmentStatus?.enabled;

        if (showReversalInvoice) {
            const tooltipTitle = "Hängt die Storno-Rechnung als PDF-Datei an die Email an. Verfügbar wenn alle Buchungen storniert sind und eine Storno-Rechnung haben.";

            return (
                <Tooltip title={tooltipTitle}>
                    <Space size="small">
                        <Checkbox style={{fontWeight: "bold"}} checked={attachReversalInvoices} onChange={(e) => setAttachReversalInvoices?.(e.target.checked)}>
                            <FilePdfOutlined style={{color: "#1890ff", marginRight: 5}} />
                            {"Storno-Rechnung anhängen"}
                        </Checkbox>
                    </Space>
                </Tooltip>
            );
        }

        // Zeige normale Rechnung (aktiviert oder deaktiviert)
        const tooltipTitle = invoiceAttachmentStatus?.enabled
            ? "Hängt die Rechnung als PDF-Datei an die Email an. Verfügbar wenn alle Buchungen abgerechnet und nicht storniert sind."
            : "Verfügbar wenn alle Buchungen abgerechnet und nicht storniert sind.";

        return (
            <Tooltip title={tooltipTitle}>
                <Space size="small">
                    <Checkbox style={{fontWeight: "bold"}} checked={attachInvoices} disabled={!invoiceAttachmentStatus?.enabled} onChange={(e) => setAttachInvoices?.(e.target.checked)}>
                        <FilePdfOutlined style={{color: invoiceAttachmentStatus?.enabled ? "#1890ff" : "#d9d9d9", marginRight: 5}} />
                        {"Rechnung anhängen"}
                    </Checkbox>
                </Space>
            </Tooltip>
        );
    };

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                alignItems: "center",
            }}
        >
            <Space>
                {showLetterPdfButton &&
                    (mailDataType === MailDataType.Course ? (
                        <Tooltip title="Alle Briefe als PDF exportieren">
                            <Button icon={<FilePdfOutlined />} onClick={handleLetterPdfClick} loading={renderBatchLetterPdfLoading}>
                                {"Als Briefe"}
                            </Button>
                        </Tooltip>
                    ) : mailDataType === MailDataType.Booking ? (
                        <Tooltip
                            title={
                                selection.length === 0
                                    ? "Mindestens eine Buchung auswählen"
                                    : selection.length === 1
                                    ? "Template als Brief-PDF exportieren"
                                    : "Alle ausgewählten Buchungen als Briefe exportieren"
                            }
                        >
                            <Button icon={<FilePdfOutlined />} onClick={handleLetterPdfClick} loading={renderLetterPdfLoading || renderBatchBookingsLetterPdfLoading} disabled={selection.length === 0}>
                                {selection.length > 1 ? "Als Briefe" : "Als Brief"}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title={selection.length !== 1 ? "Brief-Export ist nur bei genau einem Empfänger verfügbar" : "Template als Brief-PDF exportieren"}>
                            <Button icon={<FilePdfOutlined />} onClick={handleLetterPdfClick} loading={renderLetterPdfLoading} disabled={selection.length !== 1}>
                                {"Als Brief"}
                            </Button>
                        </Tooltip>
                    ))}
                <InvoiceCheckboxes />
            </Space>
            {editMode ? (
                <Space
                    style={{
                        justifyContent: "flex-end",
                    }}
                >
                    {isNoTemplate ? (
                        <Button
                            danger
                            onClick={() => {
                                setEditMode(false);
                                setSelectedTemplateUndefined?.();
                            }}
                        >
                            {"Abbrechen"}
                        </Button>
                    ) : (
                        // Zurück zur Vorschau, Entwurf bleibt erhalten; verwerfen = Dialog schließen.
                        <Button icon={<EyeOutlined />} onClick={() => setEditMode(false)}>
                            {"Vorschau"}
                        </Button>
                    )}
                    {!isNoTemplate && (
                        <Button onClick={() => handleSave()} form="emailForm" htmlType="submit" disabled={!formikProps} loading={formikProps?.isSubmitting ?? false} icon={<SaveOutlined />}>
                            {"Änderungen speichern"}
                        </Button>
                    )}
                    <SendButton editedTemplateInput={draftInput} />
                </Space>
            ) : (
                <Space
                    style={{
                        justifyContent: "flex-end",
                    }}
                >
                    <Button icon={<EditOutlined />} onClick={() => setEditMode(true)}>
                        {"Vorlage bearbeiten"}
                    </Button>
                    <SendButton editedTemplateInput={draftInput} />
                </Space>
            )}
            <LetterPdfPreviewModal
                open={letterPdfModalVisible}
                onClose={() => setLetterPdfModalVisible(false)}
                letterData={letterData}
                fileName={`Brief_${letterData?.recipientLastName || "Empfaenger"}`}
            />
            <LetterPdfBatchPreviewModal open={batchLetterPdfModalVisible} onClose={() => setBatchLetterPdfModalVisible(false)} letterDataList={batchLetterDataList} fileNamePrefix="Briefe" />
        </div>
    );
};

export default memo(EmailFooter);
